import { buildShortcutSections } from '../../utils/game/shortcutSections';
import { describeBinding, getBinding } from '../../utils/game/keybindings';
import { t, k } from '../../localization/t';

export const BOARD_TUTORIAL_KEY = 'game-board-v3';

// Medieval-styled keyboard shortcut indicator. Stone keycap with gold
// lettering, embossed shadow to mimic a carved rune. Used in the
// tutorial's shortcuts step.
const KBD_STYLE = {
  display: 'inline-block',
  padding: '1px 6px',
  background: `linear-gradient(180deg, rgba(40,34,24,0.9) 0%, rgba(20,17,12,0.95) 100%)`,
  border: `1px solid rgba(180,140,60,0.35)`,
  borderRadius: 'var(--radius-sm)',
  fontSize: '10px',
  fontFamily: 'ui-monospace, "SF Mono", "Cascadia Mono", monospace',
  fontWeight: 700,
  color: '#d4a843',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -1px 0 rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3)',
  minWidth: '20px',
  textAlign: 'center',
  lineHeight: '18px',
  letterSpacing: '0.5px',
};

export function Kbd({ children }) {
  return <span style={KBD_STYLE}>{children}</span>;
}

export function ShortcutList() {
  const SHORTCUT_SECTIONS = buildShortcutSections();
  const sectionStyle = {
    color: 'rgba(180,140,60,0.5)',
    textShadow: '0 0 8px rgba(180,140,60,0.1)',
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div className="text-xs" style={{ color: '#A6A09B' }}>
        {t('These work on the hovered card or marquee selection.')}
      </div>
      <div className="grid gap-x-6 gap-y-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
        {SHORTCUT_SECTIONS.map((section) => (
          <div key={section.label}>
            <div className="text-[0.5625cqw] font-semibold mb-1.5" style={sectionStyle}>
              {t(section.label)}
            </div>
            <div className="flex flex-col gap-1">
              {section.items.map((item) => (
                <div key={item.desc} className="flex items-center gap-2">
                  <span className="shrink-0 flex items-center gap-0.5" style={{ minWidth: '4.5cqw' }}>
                    {item.keys.map((k, i) => (
                      <span key={i}>
                        {i > 0 && <span className="text-[0.5625cqw] mx-0.5" style={{ color: 'rgba(166,160,155,0.3)' }}>+</span>}
                        <Kbd>{k}</Kbd>
                      </span>
                    ))}
                  </span>
                  <span className="text-[0.6875cqw]" style={{ color: '#A6A09B' }}>{t(item.desc)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Ordered onboarding for the game board. Steps that point at DOM
// overlays use a selector; in-canvas explanatory steps centre their
// modal instead of trying to lock onto a 3D element.
export const BOARD_TUTORIAL_STEPS = [
  {
    key: 'welcome',
    title: k('Welcome to the Table'),
    body: k('Valkenhall is a sandbox virtual tabletop: you move the cards, tokens, and dice yourself. Here\'s a quick tour of the board.'),
  },
  {
    key: 'hand',
    title: k('Your Hand'),
    body: k('Your hand sits along the bottom of the screen. Hover it to fan the cards out, then click and drag onto the table to play. Right-click any card in hand to send it to a pile.'),
    selector: '[data-tutorial="board-hand"]',
  },
  {
    key: 'piles',
    title: k('Spellbook, Atlas, Cemetery'),
    body: k('Your decks live on the sides of the table as physical piles. Click a pile to draw, or right-click for shuffle and browse options. Cards sent to the cemetery stack there automatically.'),
  },
  {
    key: 'trackers',
    title: k('Your Player Panel'),
    body: k('Your avatar panel tracks everything: HP, mana, the four elemental affinities (air, earth, fire, water), plus how many Spellbook and Atlas cards are in your hand. Base mana and the elemental affinities from your sites are tallied automatically; anything coming from card effects you adjust yourself with the + / − buttons.'),
    selector: '[data-tutorial="board-life-hud"]',
  },
  {
    key: 'pile-buttons',
    title: k('Zones'),
    body: k('This button fans out your off-board zones: Collection, your Banished pile (cards removed from play), and the Tokens spawner. Banished and Collection have a tabbed view so you can peek at your opponent\'s side too.'),
    selector: '[data-tutorial="board-pile-buttons"]',
  },
  {
    key: 'region-view',
    title: k('Surface & Subsurface'),
    body: k('Switch layers here. Subsurface shows only burrowed or submerged minions, with bodies of water highlighted. Try it.'),
    selector: '[data-tutorial="board-region-view"]',
    requireAction: true,
    actionLabel: k('Switch'),
  },
  {
    key: 'status-tokens',
    title: k('Tokens'),
    body: k('Hover this ledge to slide out the token tray. Drag any token onto the board to spawn it.'),
    selector: '[data-tutorial="board-status-tokens-ledge"]',
  },
  {
    key: 'card-preview',
    title: k('Card Preview'),
    body: k('Click this top ledge to open a pinned preview of the last card you hovered. Scroll to resize, drag to reposition. Handy for streaming.'),
    selector: '[data-tutorial="board-card-preview-ledge"]',
  },
  {
    key: 'manipulating',
    title: k('Moving Cards Around'),
    body: k('Left-click and drag any card to move it. Drag across empty space to draw a marquee selection box, then drag any card in the selection to move the whole group. Right-click a card for Tap, Flip, Delete, and pile options.'),
  },
  {
    key: 'shortcuts',
    title: k('Keyboard Shortcuts'),
    body: <ShortcutList />,
    width: 760,
  },
  {
    key: 'compendium',
    title: k('Compendium'),
    body: k('The book icon in the bottom corner opens the Compendium: a searchable reference for Sorcery rules and keywords, plus the full keyboard shortcut list. Hit Cmd/Ctrl+F anywhere to jump in.'),
    selector: '[data-tutorial="board-compendium"]',
  },
  {
    key: 'menu',
    title: k('Session Menu'),
    body: k('The burger menu in the top-left opens session controls: save the current table state, end the match, or exit back to the main menu.'),
    selector: '[data-tutorial="board-menu"]',
  },
  {
    key: 'fair-play',
    title: k('You Decide Who Won'),
    body: k('Valkenhall is a tabletop, not a rules engine. We help with base mana and site affinity, but card effects, triggers, and win conditions are up to you. At the end of a match, you and your opponent decide together who won. Be a good sport, play fair, and have fun.'),
  },
];

// Mandatory event tutorial: fires once, the first time the local player
// plays a unit from hand onto the field. Spotlights the played card via
// getRect (trackRect keeps the spotlight riding the mesh through camera
// pans / zooms) so the modal sits beside the card, never on top of it.
// requireAction + canAdvance:false lock the Continue button forever; the
// overlay only leaves when the taught action is detected (ring menu
// opened, see eventTutorials.completeEventTutorialOnAction).
export function buildFirstUnitTutorialSteps({ getCardRect }) {
  const [firstUnitBefore, firstUnitAfter] = t("Your unit is on the field. Try it now: hold {key} and left-click the highlighted card to open its status ring, where you can toggle statuses by hand and adjust the unit's power and movement.").split('{key}');
  return [
    {
      key: 'ring-menu',
      title: t('Your First Unit'),
      body: (
        <>
          {firstUnitBefore}<Kbd>Shift</Kbd>{firstUnitAfter}
        </>
      ),
      getRect: getCardRect,
      trackRect: true,
      requireAction: true,
      canAdvance: false,
      actionLabel: t('Try it'),
    },
  ];
}

// Mandatory event tutorial: fires once, the first time a square holds
// more than three cards and the expand button appears. One step covering
// both paths (button click and held hotkey), because performing EITHER
// completes it (cell fan opened, see completeEventTutorialOnAction).
// Built at render time so the hotkey label always reflects the player's
// current binding.
export function buildCellStackTutorialSteps({ getBadgeRect }) {
  const keyLabel = describeBinding(getBinding('board.expandSquare'));
  const [crowdedBefore, crowdedAfter] = t('Several cards now share this square. Try it now: click the expand button to fan them out so you can see and grab each card, or hold {key} and click the square to do the same from the keyboard.').split('{key}');
  return [
    {
      key: 'expand-button',
      title: t('A Crowded Square'),
      body: (
        <>
          <div>
            {crowdedBefore}<Kbd>{keyLabel}</Kbd>{crowdedAfter}
          </div>
          <div className="mt-2" style={{ color: '#A6A09B' }}>
            {t('You can rebind this key any time in Settings, under Keybindings.')}
          </div>
        </>
      ),
      getRect: getBadgeRect,
      trackRect: true,
      requireAction: true,
      canAdvance: false,
      actionLabel: t('Try it'),
    },
  ];
}
